import './FilterBar.scss';
import cn from 'classnames';
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Overlay, Tooltip } from 'react-bootstrap';
import * as uuid from 'uuid';
import {
  DefaultTokens,
  FilterBarTokens,
  FilterColumn,
  Option,
  parseRawTokenArray
} from '@blisspointmedia/bpm-types/dist/FilterBar';
import { Size, SizeType, Placement } from '../designConstants';
import { SegmentedControlButton } from '../SegmentedControlButton';
import { AdvancedFilterBar } from './AdvancedFilterBar';
import { BasicFilterBar } from './BasicFilterBar';
import { useMalformed } from './filterbar-utils';
import { FilterBarHelp } from './FilterBarHelp';

interface FilterBarProps<T = object> {
  advanced?: boolean;
  className?: string;
  defaultAdvanced?: boolean;
  defaultTokens?: DefaultTokens;
  disableAutoFilter?: boolean;
  dynamicTokens?: string[];
  hasAdvanced?: boolean;
  initialValue?: string;
  lines?: T[];
  onChange?: (newTokens: FilterBarTokens) => void;
  onFilter: (filter: (line: T) => boolean, tokens: Option[]) => void;
  onSetAdvanced?: (value: boolean) => void;
  options: string[] | FilterColumn[];
  placeholder?: string;
  setDynamicTokens?: (dynamicTokens: string[]) => void;
  size?: SizeType;
  // Disambiguates the sessionStorage key when a page renders more than one filterable table at the
  // same pathname (the key is otherwise pathname-only) -- without it, two tables on one page share
  // one saved-filter/mode slot and silently clobber/misapply each other's state on mount.
  storageKeySuffix?: string;
  tokens?: FilterBarTokens;
  useLocalStorage?: boolean;
  width?: number;
  filterOnModeChange?: boolean;
}

export const FilterBar = <T extends object>({
  advanced,
  className,
  defaultAdvanced = false,
  defaultTokens = {},
  disableAutoFilter = false,
  dynamicTokens = [],
  hasAdvanced = false,
  initialValue,
  lines = [],
  onChange = (_newTokens: FilterBarTokens) => {},
  onFilter = (_filter, _tokens) => {},
  onSetAdvanced = () => {},
  options: inputOptions,
  placeholder,
  setDynamicTokens,
  size = Size.MD,
  storageKeySuffix,
  tokens = {} as FilterBarTokens,
  useLocalStorage = true,
  width,
  filterOnModeChange = true
}: FilterBarProps<T>): ReactNode => {
  const options = useMemo(() => {
    const result: Option[] = [];
    for (const elem of inputOptions) {
      if (typeof elem === 'string') {
        result.push({
          label: elem,
          name: elem,
          value: elem
        });
      } else if (typeof elem === 'object') {
        result.push(elem);
      }
    }
    return result;
  }, [inputOptions]);

  const barRef = useRef<HTMLDivElement>(null);
  const transformedDefaultAdvancedTokens = useMemo(() => {
    if (defaultTokens.advanced) {
      return parseRawTokenArray(defaultTokens.advanced, options, lines);
    }
    return undefined;
  }, [defaultTokens.advanced, options, lines]);

  const transformedDynamicAdvancedTokens = useMemo(() => {
    if (dynamicTokens) {
      return parseRawTokenArray(dynamicTokens, options, lines);
    }
    return undefined;
  }, [dynamicTokens, options, lines]);

  const modeStorageKey = `filter_state_${window.location.pathname}${storageKeySuffix ? `:${storageKeySuffix}` : ''}`;

  const isAdvancedStored = useMemo(() => {
    try {
      if (!useLocalStorage) {
        return false;
      }
      return JSON.parse(window.sessionStorage.getItem(modeStorageKey) ?? '');
    } catch {
      return false;
    }
  }, [useLocalStorage, modeStorageKey]);

  const [localAdvancedMode, setLocalAdvancedMode] = useState(
    window.sessionStorage.getItem(modeStorageKey) === null ? defaultAdvanced : isAdvancedStored
  );

  const [showHelp, setShowHelp] = useState(false);
  const { malformedObject, showMalformed, setShowMalformed, setMalformed, dismissMalformed } =
    useMalformed();

  const advancedMode = !hasAdvanced ? false : (advanced ?? localAdvancedMode);
  const setAdvancedMode = useCallback(
    (setting: boolean) => {
      onSetAdvanced(setting);
      setLocalAdvancedMode(setting);

      window.sessionStorage.setItem(modeStorageKey, JSON.stringify(setting));
    },
    [onSetAdvanced, modeStorageKey]
  );

  const onBasicChange = useCallback(
    (basic: Option[]) => {
      onChange({ ...tokens, basic });
    },
    [onChange, tokens]
  );

  const onAdvancedChange = useCallback(
    (advancedTokens: Option[]) => {
      onChange({ ...tokens, advanced: advancedTokens });
    },
    [onChange, tokens]
  );

  const hasWidth = width != null;

  return (
    <div
      className={cn('filter-bar-component-container', size, className, {
        'auto-width': !hasWidth
      })}
      ref={barRef}
      style={hasWidth ? { width } : undefined}
    >
      {hasAdvanced && (
        <SegmentedControlButton
          options={['Advanced', 'Basic']}
          selectedOption={advancedMode ? 'Advanced' : 'Basic'}
          onChange={option => {
            setAdvancedMode(option === 'Advanced');
          }}
          size={size}
          className={advancedMode ? 'advanced-on' : 'basic-on'}
        />
      )}
      {advancedMode ? (
        <AdvancedFilterBar
          defaultTokens={transformedDefaultAdvancedTokens ?? []}
          disableAutoFilter={disableAutoFilter}
          dismissMalformed={dismissMalformed}
          dynamicTokens={transformedDynamicAdvancedTokens ?? []}
          initialValue={initialValue}
          lines={lines}
          onChange={onAdvancedChange}
          onFilter={onFilter}
          options={options}
          placeholder={placeholder}
          setDynamicTokens={setDynamicTokens ?? ((_dt: string[]) => {})}
          setMalformed={setMalformed}
          setShowHelp={setShowHelp}
          size={size}
          storageKeySuffix={storageKeySuffix}
          tokens={tokens.advanced}
          useLocalStorage={useLocalStorage}
          filterOnModeChange={filterOnModeChange}
        />
      ) : (
        <BasicFilterBar
          defaultTokens={(defaultTokens.basic ?? []).map(v => ({ value: v, label: v }))}
          disableAutoFilter={disableAutoFilter}
          initialValue={initialValue}
          onChange={onBasicChange}
          onFilter={onFilter}
          options={options}
          placeholder={placeholder}
          size={size}
          storageKeySuffix={storageKeySuffix}
          tokens={tokens.basic}
          useLocalStorage={useLocalStorage}
          filterOnModeChange={filterOnModeChange}
        />
      )}
      <Overlay target={barRef} show={showMalformed} placement={Placement.BOTTOM_START}>
        {overlayProps => (
          <Tooltip
            id={uuid.v4()}
            {...overlayProps}
            style={{
              ...overlayProps.style,
              cursor: 'pointer'
            }}
            onClick={() => {
              setShowMalformed(false);
            }}
          >
            {malformedObject?.message ?? ''}
          </Tooltip>
        )}
      </Overlay>
      <FilterBarHelp show={showHelp} setShowHelp={setShowHelp} />
    </div>
  );
};
